import ldOrderBy from "lodash-es/orderBy";

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef
} from "@angular/core";
import { NgClass, NgTemplateOutlet } from "@angular/common";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";

import { LgTranslatePipe } from "@logex/framework/lg-localization";
import { LgArrowNavigationDirective, LgIconComponent } from "@logex/framework/ui-core";

import { LgSlideoutService } from "../lg-slideout/lg-slideout.service";
import { LgSlideoutApi, LgSlideoutPanel } from "../lg-slideout/lg-slideout.types";

@Component({
    selector: "lg-slideout-holder-header",
    standalone: true,
    templateUrl: "./lg-slideout-holder-header.component.html",
    imports: [
        NgClass,
        NgTemplateOutlet,
        LgTranslatePipe,
        LgIconComponent,
        LgArrowNavigationDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LgSlideoutHolderHeaderComponent implements OnInit, OnDestroy {
    private _changeDetectorRef = inject(ChangeDetectorRef);

    protected _slideoutService = inject(LgSlideoutService);

    protected pinned = false;
    protected hidePin = false;
    protected iconsTemplate: TemplateRef<any> | null = null;
    protected tabButtonTemplate: TemplateRef<any> | null = null;
    protected headerTemplate: TemplateRef<any> | null = null;
    protected activeStandalone = false;
    protected activePanel: LgSlideoutPanel | null = null;
    protected _availablePanels: LgSlideoutPanel[] = [];
    private _destroyed$ = new Subject<void>();

    /**
     * Slideout API service
     */
    @Input("slideoutApi") _slideoutApi: LgSlideoutApi | null = null;

    /**
     * Which slot this header belongs to — controls which service property is used
     * to determine the active panel. Defaults to "overlay".
     */
    @Input() slot: "overlay" | "pinned" = "overlay";

    /**
     * Emits on pin toggle
     */
    @Output("pinToggled") readonly pinToggled = new EventEmitter<void>();

    ngOnInit(): void {
        if (this._slideoutApi == null) return;
        this._slideoutApi.state$.pipe(takeUntil(this._destroyed$)).subscribe(state => {
            this.pinned = state.pinned;
            this.hidePin = state.hidePin ?? false;
            this._changeDetectorRef.markForCheck();
        });
        this._slideoutService.availablePanelsState$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(panels => {
                // service.currentPanelVariant / currentOverlayPanelVariant are set
                // synchronously inside _setActivePanel BEFORE availablePanelsState$.next(),
                // so they are always up-to-date here.
                const activePanelVariant =
                    this.slot === "overlay"
                        ? this._slideoutService.currentOverlayPanelVariant
                        : this._slideoutService.currentPanelVariant;
                const activePanel = panels[activePanelVariant!];
                this.iconsTemplate = activePanel?.headerIconsTemplate ?? null;
                this.tabButtonTemplate = activePanel?.headerTabButtonTemplate ?? null;
                this.headerTemplate = activePanel?.headerTemplate ?? null;
                this.activeStandalone = activePanel?.standalone ?? false;
                this.activePanel = activePanel ?? null;
                this._availablePanels = ldOrderBy(
                    Object.values(panels || {}).filter(p => !p.standalone),
                    x => x.order ?? 0
                );
                this._changeDetectorRef.markForCheck();
            });
    }

    _changePanel(panel: LgSlideoutPanel): void {
        const activePanelVariant =
            this.slot === "overlay"
                ? this._slideoutService.currentOverlayPanelVariant
                : this._slideoutService.currentPanelVariant;
        if (panel.panelVariant === activePanelVariant) {
            this._hide(panel.panelVariant);
            return;
        }
        this._slideoutApi?.setActivePanel(panel.panelVariant);
    }

    _hide(panelVariant?: string): void {
        if (panelVariant) this._slideoutApi?.setActivePanel(panelVariant);
        this._slideoutApi?.toggleExpanded(false);
    }

    _togglePin(): void {
        this._slideoutApi?.togglePinned();
    }

    _keepKeyOrder(): number {
        return 0;
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }
}
